<script lang="ts">
  import { onMount, onDestroy } from 'svelte';

  import AnimatedSquares from './animated-squares.svelte';
  import SquaresTitles from './squares-titles.svelte';
  export let element: HTMLElement = undefined;

  const phrases = [
    { title: 'HOME.TITLE1', secondButton: 'HOME.SEE_MORE', preventAction: true },
    { title: 'HOME.TITLE2', secondButton: 'HOME.OUR_PROCESS' },
    { title: 'HOME.TITLE3', secondButton: 'HOME.OUR_PROCESS' },
    { title: 'HOME.TITLE4', secondButton: 'HOME.OUR_PROCESS' },
    { title: 'HOME.TITLE5', secondButton: 'HOME.OUR_PROCESS' },
    { title: 'HOME.TITLE6', firstButton: 'HOME.LETS_TALK', secondButton: 'HOME.OUR_PROCESS' },
  ];

  // This value must match the container size on the SCSS
  const numSlides = phrases.length;

  // current slide/step
  let currentSlide = 0;

  // progress on each slide
  // this number goes from 0 to 1. 0 is the start, 1 is the end.
  let normalizedProgress = 0;

  // absolute scroll position on the page
  let absoluteScrollPosition;

  // total height of the animated squares flow
  let totalHeight = null;

  // opacity value when fading in and out
  let fadeOpacity: string | number = 1;

  $: {
    if (absoluteScrollPosition !== undefined) {
      currentSlide = getCurrentSlide();

      // this is a progress for each "step" of the interaction
      // this number goes from 0 to 1;
      normalizedProgress = getProgressForEachStep();

      // avoid showing small gaps when the user scrolls back up
      preventSmallGapsOnScrollingThrough();

      fadeInAndOutCalculation();
    }
  }

  onMount(() => {
    updateValues();

    window.addEventListener('resize', handleResize);
  });

  onDestroy(() => {
    window.removeEventListener('resize', handleResize);
  });

  // returns value from 0 to 1
  const getProgressForEachStep = () => {
    return absoluteScrollPosition >= totalHeight
      ? 1
      : ((absoluteScrollPosition / totalHeight) * numSlides) % 1;
  };

  const preventSmallGapsOnScrollingThrough = () => {
    if (currentSlide === 0 && normalizedProgress < 0.03) {
      normalizedProgress = 0.0;
    }
  };

  const fadeInAndOutCalculation = () => {
    // normalizedProgress goes from 0 to 1, but is used for both fade in and out
    // fadeIn < 0.5 < fadeOut < 1
    // as each fade transitions only 0.5 on the progress, we multiply it by 2
    // using arbitrary numbers near '2' to speed up or down
    const multiplier = normalizedProgress < 0.5 ? 2.15 : 1.9;
    const fadeProgress = 1 - normalizedProgress * multiplier;
    fadeOpacity = easeInOutQuart(fadeProgress).toFixed(2);
  };

  /**
   * Returns a easing curve based on a given progress
   * taken from: https://easings.net/#easeInOutQuart
   */
  const easeInOutQuart = (progress) => {
    return progress < 0.5
      ? 8 * progress * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 4) / 2;
  };

  const handleResize = () => {
    updateValues();
  };

  const updateValues = () => {
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);

    totalHeight = document.getElementById('animated-flow').getBoundingClientRect().height;

    currentSlide = getCurrentSlide();
  };

  const getCurrentSlide = () => {
    // Calculates the current slide based on the scroll position and flow height
    // Math.max is needed to avoid negative index on safari with scroll bounce
    const calculatedSlide = Math.max(
      Math.floor((absoluteScrollPosition / totalHeight) * numSlides),
      0
    );

    // Prevent selecting a slide above numSlides
    return Math.min(calculatedSlide, numSlides - 1);
  };
</script>

<svelte:window bind:scrollY={absoluteScrollPosition} />
<section class="bg-gray pb-64 relative">
  <div
    id="animated-flow"
    class="animated-flow__container  md2:pl-[12.5%] relative flex flex-col w-[100%]   md2:flex-row md2:justify-between"
  >
    <div
      id="animated-squares-container"
      style="opacity: {absoluteScrollPosition > totalHeight ? 0 : 1}"
      class="fixed animated-flow__squares pointer-events-none md2:top-[50vh] md2:-translate-y-1/2 md2:right-[12.5%] w-[100%] md2:w-[fit-content] pt-[150px] h-xs:pt-[88px] h-sm:pt-[120px] h-md:pt-[230px] sm:pt-[168px] md:pt-[219px] md2:pt-0 md2:self-center md2:items-center bg-gray z-10"
    >
      <AnimatedSquares class="mx-auto" slide={currentSlide} progress={normalizedProgress} />
    </div>

    <div
      id="phrases-container"
      style="opacity: {absoluteScrollPosition > totalHeight - 100 ? 0 : 1}"
      class="h-full animated-flow__phrases  flex flex-col items-center md2:items-start justify-start md2:justify-center transition-opacity duration-400 md2:pt-0"
    >
      {#each phrases as p, i}
        <!-- TODO: only change opacity of visible elements -->
        <div
          style="opacity: {fadeOpacity}"
          class={i === 0
            ? 'h-full flex flex-col items-center mb-16 md2:mb-0 pt-0 md2:items-start justify-start md2:justify-center'
            : 'h-full flex flex-col items-center md2:items-start justify-start md2:justify-center'}
        >
          <SquaresTitles slide={i+1} title={p.title} firstButton={p.firstButton} secondButton={p.secondButton} preventAction={p.preventAction || false} />
        </div>
      {/each}
    </div>
  </div>
  <span class="absolute bottom-[72px]" bind:this={element} />
</section>

<style global lang="scss">
  .animated-flow {
    &__container {
      height: calc(100vh * 6);

      @media (max-width: 1023px) {
        height: calc(70vh * 6);
      }
    }

    &__squares {
      background-color: transparent;
      background-image: linear-gradient(to bottom, rgb(240, 240, 240) 0%, rgb(240, 240, 240) 90%, rgba(240, 240, 240, 0) 100%);
      transition: 295ms ease opacity;
      z-index: 1;
    }

    &__phrases {
      // These are used to maintain compatibility with previous layout
      // I'm using transform because padding adds to the totalHeight and breaks the flow
      // pt-[414px] sm:pt-[424px] h-xs:pt-[352px] h-sm:pt-[436px] h-md:pt-[558px] md:pt-[573px]
      transform: translateY(0px);

      @media (min-width: 320px) {
        transform: translateY(424px);
      }

      @media (min-width: 360px) {
        transform: translateY(436px);
      }

      @media (min-height: 640px) and (max-width: 714px) {
        transform: translateY(436px);
      }

      @media (min-width: 714px) {
        transform: translateY(590px);
      }

      @media (min-height: 768px) and (max-width: 714px) {
        transform: translateY(573px);
      }

      @media (min-height: 920px) {
        transform: translateY(700px);
      }

      @media (min-width: 1023px) {
        transform: translateY(0px);
      }
    }
  }
</style>
